import { ApiError, type AmoyeApi, type ClassificationJob, type ClassificationJobSummary,
  type EvidenceWorkflowProposal, type ResolvedIdentityContext } from '../../lib/api';
import { applyEvidenceDuplicatePresentation, applyEvidenceProposalBlockers, applyExtractedOwnership,
  classificationSourceUnavailable, reconciliationFromResult, settleEvidence, settleEvidenceWorkflowError } from './evidence-results';
import { detectEvidenceOwnerFromFilename } from './evidence-ownership';
import type { EvidenceItem, ReconciliationItem } from './types';

export interface EvidenceRecoveryOwner {
  id: string;
  name: string;
  addedBy: string;
}

export interface RecoveredEvidence {
  evidence: EvidenceItem;
  reconciliation: ReconciliationItem[];
  replaceReconciliation: boolean;
}

/** Rebuilds the browser read model exclusively from server-scoped durable
 * records. It never stores taxpayer/job identifiers outside the authenticated
 * application and it reuses the original job idempotency keys when a session
 * ended between secure intake and proposal creation. */
export async function restorePersistedEvidence(api: AmoyeApi, context: ResolvedIdentityContext,
  owner: EvidenceRecoveryOwner,
  onRecovered: (value: RecoveredEvidence) => void, signal?: AbortSignal): Promise<void> {
  const { jobs } = await api.listClassificationJobs(owner.id, signal, context.contextId);
  for (const job of jobs) onRecovered({ evidence: evidenceFromJob(job, owner), reconciliation: [],
    replaceReconciliation: false });
  await Promise.all(jobs.map(async (summary) => {
    try {
      const recovered = await recoverJob(api, context, summary, owner, signal);
      if (!signal?.aborted) onRecovered(recovered);
    } catch (error) {
      if (signal?.aborted || error instanceof DOMException && error.name === 'AbortError') return;
      if (error instanceof ApiError && error.status === 401) throw error;
      onRecovered({ evidence: settleEvidenceWorkflowError(evidenceFromJob(summary, owner), error),
        reconciliation: [], replaceReconciliation: false });
    }
  }));
}

export function mergeRecoveredEvidence<T extends { evidence: EvidenceItem[]; reconciliation: ReconciliationItem[] }>(
  current: T, recovered: RecoveredEvidence): T {
  const evidence = [recovered.evidence, ...current.evidence.filter((item) => item.id !== recovered.evidence.id)];
  const reconciliation = recovered.replaceReconciliation
    ? [...recovered.reconciliation,
      ...current.reconciliation.filter((item) => item.evidenceId !== recovered.evidence.id)]
    : current.reconciliation;
  return { ...current, evidence, reconciliation };
}

async function recoverJob(api: AmoyeApi, context: ResolvedIdentityContext,
  summary: ClassificationJobSummary, owner: EvidenceRecoveryOwner, signal?: AbortSignal): Promise<RecoveredEvidence> {
  const job: ClassificationJob = summary.status === 'pending' || summary.status === 'processing'
    ? await api.waitForJob(summary.job_id, { signal, contextId: context.contextId }) : summary;
  const base = evidenceFromJob(summary, owner);
  if (job.status === 'failed') {
    if (!summary.document_available || classificationSourceUnavailable(job)) {
      return { evidence: settleEvidence(base, job), reconciliation: [], replaceReconciliation: true };
    }
    const promoted = await api.prepareClassificationEvidenceForHumanReview(summary, job.job_id, signal, context);
    const human = await api.readHumanEvidenceClassification(promoted.evidenceId, signal, context);
    const evidence = human.humanDecisionAuthoritativeForClassification
      ? { ...settleEvidence(base, job), state: 'reviewed' as const, ownershipState: 'confirmed' as const,
        classificationCategory: human.evidenceCategory,
        issue: 'A person reviewed this source and recorded its filing category after automated processing could not classify it.',
        serverEvidenceId: promoted.evidenceId, duplicateOfEvidenceId: promoted.duplicateOfEvidenceId }
      : { ...settleEvidence(base, job), serverEvidenceId: promoted.evidenceId,
        duplicateOfEvidenceId: promoted.duplicateOfEvidenceId,
        issue: `${settleEvidence(base, job).issue ?? ''} Review the source and classify it manually if Amoye’s assessment is not correct.`.trim() };
    return { evidence, reconciliation: [], replaceReconciliation: true };
  }
  const result = await api.result(job.job_id, signal, context.contextId);
  const promoted = await api.resumeClassificationEvidence(summary, signal, context);
  if (promoted.processingRevoked) return { evidence: { ...settleEvidence(base, job), state: 'unlinked',
    issue: 'This source was previously removed from active tax use. Its custody record remains available for audit.',
    serverEvidenceId: promoted.evidenceId,
    ...(promoted.processingGrantId ? { processingGrantId: promoted.processingGrantId } : {}) },
    reconciliation: [], replaceReconciliation: true };
  const human = await api.readHumanEvidenceClassification(promoted.evidenceId, signal, context);
  const ownership = applyExtractedOwnership(settleEvidence(base, job), result, owner);
  const ownershipProposals = (promoted.proposalsByItemId?.[job.job_id] ?? [])
    .filter((proposal) => proposal.fieldPath.endsWith('.ownership_state'));
  const ownershipDecision = latestDecision(ownershipProposals);
  const recoveredEvidence = human.humanDecisionAuthoritativeForClassification
    ? { ...ownership.evidence, ownershipState: 'confirmed' as const,
      classificationCategory: human.evidenceCategory, state: 'ready-for-review' as const,
      issue: 'A person reviewed this source and recorded its taxpayer and filing category.' }
    : ownershipDecision?.action === 'CORRECT_AND_ACCEPT'
    ? { ...ownership.evidence, ownershipState: 'confirmed' as const, state: 'ready-for-review' as const,
      issue: 'Ownership was confirmed previously. The recorded review state has been restored.' }
    : ownership.evidence;
  const reconciliation = reconciliationFromResult(result, summary.filename, job.job_id)
    .filter((proposal) => !human.humanDecisionAuthoritativeForClassification || proposal.id !== job.job_id)
    .map((proposal) => {
    const serverProposals = (promoted.proposalsByItemId?.[proposal.id] ?? [])
      .filter((candidate) => !candidate.fieldPath.endsWith('.ownership_state'));
    const linked = applyEvidenceProposalBlockers({ ...proposal,
      serverProposalIds: serverProposals.length ? serverProposals.map((candidate) => candidate.id)
        : promoted.proposalIdsByTransactionId[proposal.id] ?? [], serverProposals },
    recoveredEvidence, promoted.duplicateOfEvidenceId);
    return restoreReviewState(linked, serverProposals);
  });
  const allReviewed = reconciliation.length > 0
    && reconciliation.every((item) => ['accepted-by-human', 'corrected-by-human', 'excluded-by-human'].includes(item.state));
  const presentedEvidence = human.humanDecisionAuthoritativeForClassification
    ? recoveredEvidence : applyEvidenceDuplicatePresentation(recoveredEvidence, promoted.duplicateOfEvidenceId);
  const evidence = { ...presentedEvidence,
    ...(allReviewed ? { state: 'reviewed' as const,
      issue: 'Every extracted proposal from this source has a recorded human decision.' } : {}),
    serverEvidenceId: promoted.evidenceId,
    ...(promoted.processingGrantId ? { processingGrantId: promoted.processingGrantId } : {}),
    ...(ownershipProposals.length ? { serverOwnershipProposals: ownershipProposals } : {}) };
  return { evidence, reconciliation, replaceReconciliation: true };
}

function evidenceFromJob(job: ClassificationJobSummary, owner: EvidenceRecoveryOwner): EvidenceItem {
  const ownership = detectEvidenceOwnerFromFilename(job.filename, { id: owner.id, name: owner.name });
  const base: EvidenceItem = { id: job.job_id, name: job.filename, sourceType: evidenceSource(job.filename),
    state: job.status === 'failed' ? 'processing-failed' : 'checking', addedBy: owner.addedBy,
    addedAt: new Date(job.created_at).toLocaleString('en-NG'),
    hashLabel: job.content_sha256 ? `SHA-256 · …${job.content_sha256.slice(-8)}` : 'Integrity check recorded',
    documentAvailable: job.document_available, taxpayerId: owner.id, taxpayerName: owner.name,
    ownershipState: ownership.state,
    ...(ownership.detectedName ? { ownershipProposal: { detectedName: ownership.detectedName,
      basis: ownership.detectedBy ?? 'browser-source-inspection', extractor: 'browser-source-inspection/2' } } : {}) };
  return job.status === 'failed' ? settleEvidence(base, job)
    : job.status === 'completed' ? settleEvidence(base, job) : base;
}

function evidenceSource(name: string): EvidenceItem['sourceType'] {
  const extension = name.toLowerCase().split('.').at(-1);
  if (extension === 'pdf') return 'bank-statement';
  if (['png', 'jpg', 'jpeg', 'webp'].includes(extension ?? '')) return 'receipt';
  if (['xlsx', 'json'].includes(extension ?? '')) return 'pos-export';
  return 'controlled-csv';
}

function latestDecision(proposals: readonly EvidenceWorkflowProposal[]) {
  return proposals.map((proposal) => proposal.reviewDecision).filter((decision) => decision !== undefined)
    .sort((left, right) => right.recordedAt.localeCompare(left.recordedAt))[0];
}

function restoreReviewState(item: ReconciliationItem,
  proposals: readonly EvidenceWorkflowProposal[]): ReconciliationItem {
  if (item.state === 'blocked') return item;
  const decisions = proposals.map((proposal) => proposal.reviewDecision);
  if (decisions.length === 0 || decisions.some((decision) => !decision)) return item;
  const actions = decisions.map((decision) => decision!.action);
  if (actions.includes('DEFER_BLOCKING')) return { ...item, state: 'blocked',
    dispositionReason: 'A prior human decision deferred this item.' };
  if (actions.every((action) => action === 'REJECT_PROPOSAL')) return { ...item, state: 'excluded-by-human' };
  if (actions.includes('CORRECT_AND_ACCEPT')) return { ...item, state: 'corrected-by-human' };
  return { ...item, state: 'accepted-by-human' };
}
